import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LessonCollectableCategoryListStyles } from "./lesson-collectable-category-list.styles";
import { LessonCollectableCategory } from "../lesson-collectable-category";
import {
  getParticipantLessonCategories,
  addLessonCollectable,
  getLessonCollectableBySlideId,
} from "../../learning-path/learningPath.service";
import { GamePlanSlide } from "../wizard/use-game-plan";

export interface Category {
  id?: number;
  name: string;
  isPrivate: boolean;
}

const defaultCategory: Category[] = [
  {
    id: null,
    name: "All",
    isPrivate: false,
  },
];

interface LessonCollectableCategoryListProps {
  slide: GamePlanSlide;
  onLessonCollectableSaveSuccess?: () => void;
}

export const LessonCollectableCategoryList = (
  props: LessonCollectableCategoryListProps
) => {
  const { slide, onLessonCollectableSaveSuccess } = props;
  const classes = LessonCollectableCategoryListStyles();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["participant-lesson-category"],
    queryFn: () => getParticipantLessonCategories(),
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const { data: lessonCollectableData } = useQuery({
    queryKey: ["participant-lesson-collectable", slide.id],
    queryFn: () => getLessonCollectableBySlideId(slide.id),
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const queryClient = useQueryClient();

  const { mutate, isLoading: isUpdating } = useMutation(addLessonCollectable, {
    retry: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["participant-lesson-collectable", slide.id],
      });

      onLessonCollectableSaveSuccess();
    },
  });

  const handleSaveCollectable = (catetoryId) => {
    if (!lessonCollectableData?.id) {
      const request = {
        categoryId: catetoryId > 0 ? catetoryId : null,
        slideId: slide.id,
      };

      mutate(request);
    }
  };

  const sortCategories = (rawData) => {
    if (!rawData) {
      return [];
    }

    return rawData
      .map((d) => ({
        priority: d.name.toLowerCase() === "all" ? 0 : 1,
        ...d,
      }))
      .sort((a, b) => {
        if (a.priority === b.priority) {
          return a.name > b.name ? 1 : -1;
        }

        return a.priority - b.priority;
      });
  };

  const categoryList = () => {
    let categories = defaultCategory;
    if (data?.length > 0) {
      categories = sortCategories(data);
    }

    return categories.map((category) => {
      const { id, name, isPrivate } = category;

      return (
        <LessonCollectableCategory
          key={id}
          id={id}
          name={name}
          isPrivate={isPrivate}
          onCategorySelected={handleSaveCollectable}
          isSaveEnabled={!lessonCollectableData?.id && !isUpdating}
          isSelected={
            lessonCollectableData?.id &&
            lessonCollectableData?.categoryId === category.id
          }
        />
      );
    });
  };

  return (
    <>
      {(isLoading || isFetching) && <div>Loading...</div>}
      {data && <div className={classes.scrollingDiv}>{categoryList()}</div>}
    </>
  );
};
